import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import colores from "../../shared/constants/styles";
import {
  descargarTemplate,
  importarProductos,
  confirmarImportacion,
  limpiarEstado,
} from "../../actions/importadorProductos";
import { IImportadorProductosProps } from "./interface/IImportadorProductos";
import Dolar from "../../common/dolar";
import Title from "../commons/header_title";

interface IImportStep {
  upload: 0;
  confirmacion: 1;
  success: 2;
}

const ImportadorProductos = (props: IImportadorProductosProps & any) => {
  const dispatch = useDispatch();
  const [formDataFile, setFormDataFile] = useState<{ file: File[] }>({
    file: [],
  });
  const [step, setStep] = useState<number>(0);
  const [erroresArchivo, setErroresArchivo] = useState<string[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

  useEffect(() => {
    if (props.success && step === 0) {
      setStep(1);
    }
    if (props.success && step === 1) {
      setStep(2);
    }
  }, [props.success]);

  const handleDescargarTemplate = () => {
    dispatch(descargarTemplate());
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErroresArchivo([]);
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !validExtensions.includes(fileExtension)) {
      setErroresArchivo([
        "El archivo debe ser en formato Excel (.xlsx o .xls)",
      ]);
      return;
    }

    setFormDataFile({ file: [file] });
    setErroresArchivo([]);
  };

  const handleImportar = () => {
    if (formDataFile.file.length === 0) {
      setErroresArchivo(["Por favor selecciona un archivo"]);
      return;
    }
    dispatch(importarProductos(formDataFile));
  };

  const handleConfirmar = () => {
    if (
      props.productosEncontrados &&
      props.productosEncontrados.$values.length > 0
    ) {
      dispatch(confirmarImportacion());
    }
  };

  const handleAbrirConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleCerrarConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmarImportacion = () => {
    setOpenConfirmDialog(false);
    handleConfirmar();
  };

  const handleCancelar = () => {
    dispatch(limpiarEstado());
    setFormDataFile({ file: [] });
    setStep(0);
    setErroresArchivo([]);
  };

  const handleEliminarArchivo = () => {
    setFormDataFile({ file: [] });
    setErroresArchivo([]);
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const tieneErroresCriticos =
    props.productosNoEncontrados &&
    props.productosNoEncontrados.$values?.length > 0;
  const productosValidos = (props.productosEncontrados.$values || []).length;

  return (
    <div
      style={{
        backgroundColor: colores.grisClaro,
        overflow: "hidden",
        clear: "both",
      }}
    >
      <div>
        <Dolar></Dolar>
        <Title title={"IMPORTAR CARRITO"}></Title>
      </div>
      <div
        className="container-fluid mx-0 px-0 w-100"
        style={{ margin: "2rem" }}
      >
        <div
          className="container-fluid"
          style={{
            backgroundColor: "white",
            width: "100%",
            overflow: "hidden",
            clear: "both",
            marginBottom: "1.85rem",
            paddingLeft: "76px",
            paddingRight: "76px",
            paddingBottom: "76px",
          }}
        >
          <Card sx={{ mb: 3, marginTop: "1.85rem" }}>
            <CardContent>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: "bold", color: colores.azulOscuro }}
              >
                Importador de Productos
              </Typography>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Importa múltiples productos a tu carrito usando un archivo Excel
              </Typography>
            </CardContent>
          </Card>
          {/* PASO  1*/}
          {step === 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3}>
                {/* Descargar Template */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      border: `1px solid ${colores.grisClaro}`,
                      borderRadius: 2,
                      textAlign: "center",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      Paso 1: Descargar Template
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mb: 2, color: "gray" }}
                    >
                      Descarga el archivo Excel con las columnas necesarias y
                      complétalo con tus productos
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<CloudDownloadIcon />}
                      onClick={handleDescargarTemplate}
                      disabled={props.loadingDownload}
                      sx={{
                        backgroundColor: colores.rojo,
                        color: "white",
                        borderColor: colores.rojo,
                        "&:hover": {
                          backgroundColor: "white",
                          color: colores.rojo,
                          borderColor: colores.rojo,
                        },
                      }}
                    >
                      {props.loadingDownload
                        ? "Descargando..."
                        : "Descargar Template"}
                    </Button>
                  </Box>
                </Grid>

                {/* Upload Archivo */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      border: `2px dashed ${colores.azulOscuro}`,
                      borderRadius: 2,
                      textAlign: "center",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ mb: 2, fontWeight: "bold" }}
                    >
                      Paso 2: Cargar Archivo
                    </Typography>

                    <input
                      id="file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />

                    {formDataFile.file.length === 0 ? (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 2, color: "gray" }}
                        >
                          Selecciona el archivo Excel completado
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{
                            backgroundColor: "white",
                            color: colores.rojo,
                            borderColor: colores.rojo,
                            "&:hover": {
                              backgroundColor: colores.rojo,
                              color: "white",
                              borderColor: colores.rojo,
                            },
                          }}
                          onClick={() =>
                            document.getElementById("file-input")?.click()
                          }
                        >
                          Seleccionar Archivo
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {formDataFile.file[0].name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={handleEliminarArchivo}
                          sx={{ color: "red" }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}

                    {erroresArchivo.length > 0 && (
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        {erroresArchivo.map((error, idx) => (
                          <Alert key={idx} severity="error">
                            {error}
                          </Alert>
                        ))}
                      </Stack>
                    )}

                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleImportar}
                        disabled={
                          formDataFile.file.length === 0 || props.loading
                        }
                        sx={{
                          backgroundColor: colores.rojo,
                          color: "white",
                          borderColor: colores.rojo,
                          "&:hover": {
                            backgroundColor: "white",
                            color: colores.rojo,
                            borderColor: colores.rojo,
                          },
                        }}
                      >
                        {props.loading ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Importar"
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                {/* Error General */}
                {props.error && props.errorMessage && (
                  <Grid item xs={12}>
                    <Alert severity="error">{props.errorMessage}</Alert>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* PASO 2 */}
          {step === 1 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "bold", color: colores.azulOscuro }}
              >
                Resumen de Validación
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card sx={{ backgroundColor: "#e8f5e9" }}>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Productos
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ color: "#2e7d32", fontWeight: "bold" }}
                      >
                        {productosValidos}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      backgroundColor: tieneErroresCriticos
                        ? "#ffebee"
                        : "#e3f2fd",
                    }}
                  >
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Errores
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          color: tieneErroresCriticos ? "#c62828" : "#1565c0",
                          fontWeight: "bold",
                        }}
                      >
                        {props.productosNoEncontrados?.$values?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {tieneErroresCriticos && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Se encontraron{" "}
                  {props.productosNoEncontrados?.$values?.length || 0}{" "}
                  producto(s) con error(es). Solo se importarán los productos
                  válidos.
                </Alert>
              )}

              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: "bold" }}
              >
                Detalle de Productos:
              </Typography>

              <TableContainer
                sx={{ mb: 3, border: `1px solid ${colores.grisClaro}` }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: colores.azulOscuro }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Código
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Nombre
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "white", fontWeight: "bold" }}
                      >
                        Cantidad
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "white", fontWeight: "bold" }}
                      >
                        Largo
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "white", fontWeight: "bold" }}
                      >
                        Ancho
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Estado
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.productosEncontrados?.$values?.map(
                      (producto: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {producto.productoId}
                          </TableCell>
                          <TableCell>{producto.descripcion}</TableCell>
                          <TableCell align="right">
                            {producto.cantidad}
                          </TableCell>
                          <TableCell align="right">
                            {producto.largo.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {producto.ancho.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                color: "#388e3c",
                                fontWeight: "bold",
                                fontSize: 12,
                              }}
                            >
                              Válido
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                    {props.productosNoEncontrados?.$values?.map(
                      (error: any, idx: number) => (
                        <TableRow
                          key={`error-${idx}`}
                          sx={{ backgroundColor: "rgba(244, 67, 54, 0.1)" }}
                        >
                          <TableCell
                            sx={{ fontWeight: "bold", color: "#d32f2f" }}
                          >
                            {error.productoId}
                          </TableCell>
                          <TableCell colSpan={4} sx={{ color: "#d32f2f" }}>
                            {error.error}
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                color: "#d32f2f",
                                fontWeight: "bold",
                                fontSize: 12,
                              }}
                            >
                              ✗ Error
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  sx={{
                    backgroundColor: "white",
                    color: colores.rojo,
                    borderColor: colores.rojo,
                    "&:hover": {
                      borderColor: colores.rojo,
                    },
                  }}
                  onClick={handleCancelar}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAbrirConfirmDialog}
                  disabled={productosValidos === 0 || props.loading}
                  sx={{
                    backgroundColor: colores.rojo,
                    color: "white",
                    borderColor: colores.rojo,
                    "&:hover": {
                      backgroundColor: "white",
                      color: colores.rojo,
                      borderColor: colores.rojo,
                    },
                  }}
                >
                  {props.loading ? <CircularProgress size={24} /> : `Confirmar`}
                </Button>
              </Box>
            </Paper>
          )}

          {/* PASO 3 */}
          {step === 2 && (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6">Importados correctamente</Typography>
                Los producto(s) fueron agregados al carrito.
              </Alert>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: colores.rojo,
                  color: "white",
                  borderColor: colores.rojo,
                  "&:hover": {
                    backgroundColor: "white",
                    color: colores.rojo,
                    borderColor: colores.rojo,
                  },
                }}
                onClick={() => {
                  handleCancelar();
                }}
              >
                Volver a importar
              </Button>
            </Paper>
          )}
        </div>
      </div>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCerrarConfirmDialog}
      >
        <DialogTitle>Confirmar Importación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Al confirmar va a eliminar los productos del carrito que tenia anteriormente
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCerrarConfirmDialog}
            sx={{
              color: colores.rojo,
              borderColor: colores.rojo,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarImportacion}
            variant="contained"
            sx={{
              backgroundColor: colores.rojo,
              color: "white",
              "&:hover": {
                backgroundColor: "white",
                color: colores.rojo,
                borderColor: colores.rojo,
              },
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect((state: any) => ({
  loading: state.importadorProductos?.loading,
  loadingDownload: state.importadorProductos?.loadingDownload,
  success: state.importadorProductos?.success,
  error: state.importadorProductos?.error,
  errorMessage: state.importadorProductos?.errorMessage,
  productosEncontrados: state.importadorProductos?.productosEncontrados,
  productosNoEncontrados: state.importadorProductos?.productosNoEncontrados,
  successMessage: state.importadorProductos?.successMessage,
}))(ImportadorProductos);
